import { Box, Grid2, IconButton, Typography } from '@mui/material';
import { QuickOrderAdditionalFee, FeeType, Currencies } from '../types';
import { ArrowBack } from '@mui/icons-material';
import PageWrapper from '../components/PageWrapper';
import { QuickOrderCombinedTotal } from './orders/components/OrderPaymentTotals';
import PaymentForm from './orders/components/PaymentForm';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PreviewOrder, useRetrievePendingOrderQuery } from '../services/orders';
import { useTranslation } from 'react-i18next';
import { useQuickOrderPreviewCombinedOrderMutation, useQuickOrderSubmitCombinedOrderMutation } from '../services/quickOrders';
import ShippingInfoForm, { ShippingInfo } from './orders/components/ShippingInfoForm';
import { ShippingMethod } from '../services/companies';
import { useShippingRegionContext } from '../contexts/ShippingRegionContext';
import { CheckoutOrderProducts } from './checkout/CheckoutOrderProducts';

interface PaymentIntent {
	id: string;
	clientSecret: string;
}

const CheckoutView = () => {
	const navigate = useNavigate();
	const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null);
	const [submitOrderItems, setSubmitOrderItems] = useState<{id: number, quantity: string, quickOrderId: number}[] | null>(null);
	const [previewOrderMutation, {isLoading: isOrderPreviewLoading}] = useQuickOrderPreviewCombinedOrderMutation();
	const [quickOrderSubmitMutation, {isLoading: isQuickOrderSubmitLoading, data: quickOrderSubmitResult}] = useQuickOrderSubmitCombinedOrderMutation();
	const {data: pendingOrderResult} = useRetrievePendingOrderQuery({paymentIntentId: paymentIntent?.id as string, clientSecret: paymentIntent?.clientSecret as string}, {skip: !paymentIntent});
	const [orderInfo, setOrderInfo] = useState<PreviewOrder | null>(null);
	const [combinedOrderInfo, setCombinedOrderInfo] = useState<PreviewOrder | null>(null);
	const [orderInfoAdditionalFeesWithDynamicShipping, setOrderInfoAdditionalFeesWithDynamicShipping] = useState<Pick<QuickOrderAdditionalFee, 'id' | 'name' | 'price' | 'description' | 'type'>[]>([]);
	const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
	const [shippingMethod, setShippingMethod] = useState<ShippingMethod | null>(null);
	const [country, setCountry] = useState<string | null>(null);
	const {t} = useTranslation();
	const {selectedRegion} = useShippingRegionContext();

	useEffect(() => {
		if (quickOrderSubmitResult) {
			setPaymentIntent(quickOrderSubmitResult.paymentIntent);
			localStorage.setItem('paymentIntent', JSON.stringify(quickOrderSubmitResult.paymentIntent));
		}
	}, [quickOrderSubmitResult]);

	useEffect(() => {
		const paymentIntentRef = localStorage.getItem('paymentIntent');
		const submitOrderItemsRef = localStorage.getItem('shoppingCartItems');
		if (paymentIntentRef) {
			// triggers fetch pending order hook	
			setPaymentIntent(JSON.parse(paymentIntentRef));
		} else if (submitOrderItemsRef) {
			// triggers fetch preview order hook
			setSubmitOrderItems(JSON.parse(submitOrderItemsRef));
		} else {
			navigate('/');
		}
	}, []);

	useEffect(() => {
		if (pendingOrderResult) {
			setOrderInfo(pendingOrderResult.order);
			setCombinedOrderInfo(pendingOrderResult.combinedOrder ?? null);
		}
	}, [pendingOrderResult]);

	useEffect(() => {
		if (submitOrderItems && selectedRegion) {
			const itemsByQuickOrderIds = submitOrderItems.reduce((acc, item) => {
				if (!acc[item.quickOrderId]) {
					acc[item.quickOrderId] = [] as {id: number, quantity: string}[];
				}
				acc[item.quickOrderId].push({id: item.id, quantity: item.quantity});
				return acc;
			}, {} as Record<number, {id: number, quantity: string}[]>,
			);
			previewOrderMutation({
				orders: Object.entries(itemsByQuickOrderIds).map(([quickOrderId, items]) => ({
					quickOrderId: Number(quickOrderId),
					items,
				})),
				countryCode: selectedRegion,
			}).then((res) => {
				const {combinedOrders, shippingMethods: shippingMethodsResponse, country} = res.data ?? {combinedOrders: [], shippingMethods: [], country: ''};
				const orderWithPhysicalShipping = combinedOrders.find(order => !order.isEligibleForDigitalDelivery);
				const orderWithDigitalShipping = combinedOrders.find((order) => order.isEligibleForDigitalDelivery);
				setOrderInfo(orderWithPhysicalShipping ?? orderWithDigitalShipping ?? null);
				setCombinedOrderInfo(orderWithPhysicalShipping && orderWithDigitalShipping ? orderWithDigitalShipping : null);
				setShippingMethods(shippingMethodsResponse);
				setCountry(country);
			});
		}
	}, [submitOrderItems]);
	
	// TODO: move to the block above?
	useEffect(() => {
		if (shippingMethods?.length) {
			setShippingMethod(shippingMethods[0]);
		}
	}, [shippingMethods]);

	useEffect(() => {
		if (pendingOrderResult) {
			// show additional fees from pending order 
			setOrderInfoAdditionalFeesWithDynamicShipping(pendingOrderResult.order?.additionalFees ?? []);
			return;
		}

		// TODO: test this scenario
		if (!shippingMethod) {
			setOrderInfoAdditionalFeesWithDynamicShipping([...orderInfo?.additionalFees ?? []]);
			return;
		}

		// when shipping method is selected and has a price, present it as a fixed fee
		setOrderInfoAdditionalFeesWithDynamicShipping([...orderInfo?.additionalFees ?? [], {
			id: Number(`shipping-${shippingMethod.id}`),
			name: shippingMethod.variant ? `${t(`shippingMethods.${shippingMethod.method}`)} (${shippingMethod.variant})` : t(`shippingMethods.${shippingMethod.method}`),
			price: shippingMethod.price ? shippingMethod.price.toString() : '0',
			type: FeeType.Fixed,
		}]);
	}, [shippingMethod, pendingOrderResult, orderInfo]);

	const handleShippingInfoSubmit = async (shippingInfo: ShippingInfo) => {
		if (shippingMethod && submitOrderItems && selectedRegion) {
			const itemsByQuickOrderIds = submitOrderItems.reduce((acc, item) => {
				if (!acc[item.quickOrderId]) {
					acc[item.quickOrderId] = [] as {id: number, quantity: string}[];
				}
				acc[item.quickOrderId].push({id: item.id, quantity: item.quantity});
				return acc;
			}, {} as Record<number, {id: number, quantity: string}[]>,
			);
			quickOrderSubmitMutation({
				orders: Object.entries(itemsByQuickOrderIds).map(([quickOrderId, items]) => ({
					quickOrderId: Number(quickOrderId),
					items,
				})),
				shippingMethodId: shippingMethod.id,
				shippingInfo,
				countryCode: selectedRegion,
			});
		}
	};

	let displayableTotal = '0.00';
	if (orderInfo?.combinedPaymentTotal) {
		displayableTotal = (Number(orderInfo.combinedPaymentTotal)/100).toFixed(2);
	} else if (orderInfo?.total) {
		displayableTotal = (Number(orderInfo.total)/100).toFixed(2);
	}
	const defaultShippingMethodId = pendingOrderResult?.order?.shippingMethodId ?? shippingMethods?.[0]?.id;
	const shouldShowShippingInfoForm = defaultShippingMethodId && shippingMethods?.length && !pendingOrderResult;

	const totalProducts = [orderInfo?.products ?? [], combinedOrderInfo?.products ?? []].flat();
	const totalAdditionalFees = [orderInfoAdditionalFeesWithDynamicShipping ?? [], combinedOrderInfo?.additionalFees ?? []].flat();
	const isCombinedOrder = Boolean(combinedOrderInfo);

	return <PageWrapper isLoading={isOrderPreviewLoading}>
		<Box sx={{ borderBottom: 1, borderColor: 'divider' }} display='flex' flexDirection='row'>
			<IconButton onClick={() => navigate(`/plans/${orderInfo?.planId}/supplies`)} sx={{my:0.5}}>
				<ArrowBack />   
			</IconButton>
		</Box>
		<Grid2 container spacing={3}>
			<Grid2 size={{xs: 12, md: 6}}>
				<Typography variant='h6' sx={{ my: 2 }} textAlign='center'>{t('checkout.products')}</Typography>
				<Box display='flex' flexDirection='column' gap={2}>
					{combinedOrderInfo && <CheckoutOrderProducts orderInfo={combinedOrderInfo} products={combinedOrderInfo.products} additionalFees={combinedOrderInfo.additionalFees} orderIdx={isCombinedOrder ? 0 : undefined} />}
					{orderInfo && <CheckoutOrderProducts orderInfo={orderInfo} products={orderInfo.products} additionalFees={orderInfoAdditionalFeesWithDynamicShipping} orderIdx={combinedOrderInfo ? 1 : undefined} />}
				</Box>
				{isCombinedOrder && <QuickOrderCombinedTotal 
					products={totalProducts}
					additionalFees={totalAdditionalFees}
					currency={orderInfo?.currency as Currencies}
				/>}
			</Grid2>
			{orderInfo && <Grid2 size={{xs: 12, md: 6}}>
				{paymentIntent && <Typography variant='h6' sx={{ my: 2 }}>{t('checkout.customer')}</Typography>}
				{pendingOrderResult?.order && <Box display='flex' flexDirection='column' gap={2}>
					<Box>
						<Typography variant='body2'>
							{t('checkout.contactInformation')}
						</Typography>
						<Typography>
							{pendingOrderResult.order.customerFirstName} {pendingOrderResult?.order?.customerLastName}
						</Typography>
						<Typography>
							{pendingOrderResult.order.phone}
						</Typography>
						<Typography>
							{pendingOrderResult.order.customerEmail}
						</Typography>
					</Box>
					<Box>
						<Typography variant='body2'>
							{t('checkout.shippingAddress')}
						</Typography>
						<Typography>
							{[pendingOrderResult.order.customerAddress1, pendingOrderResult.order.customerAddress2].join(', ')}
						</Typography>
						<Typography>
							{[pendingOrderResult.order.customerCity, pendingOrderResult.order.customerState].join(', ')}{' '}{pendingOrderResult.order.customerPostalCode}
						</Typography>
						<Typography>
							{pendingOrderResult.order.customerCountry}
						</Typography>
					</Box>
				</Box>}
				{shouldShowShippingInfoForm && <ShippingInfoForm 
					shippingMethods={shippingMethods}
					shippingMethodDefaultValue={defaultShippingMethodId}
					onSubmit={handleShippingInfoSubmit}
					onShippingMethodChange={setShippingMethod}
					isSubmitting={isQuickOrderSubmitLoading}
					country={country}
					currency={orderInfo.currency}
				/>}
				{Boolean(paymentIntent && displayableTotal) && <Box>
					<PaymentForm 
						total={displayableTotal as string}
						paymentIntentId={(paymentIntent as PaymentIntent).id} 
						clientSecret={(paymentIntent as PaymentIntent).clientSecret}
						currency={orderInfo.currency}
						isMultipleSellers={isCombinedOrder}
					/>
				</Box>}
			</Grid2>}
		</Grid2>
	</PageWrapper>;
};

export default CheckoutView;
